import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { Card } from '@/components/ui/Card';
import { PcOnlyHintModal } from '@/components/PcOnlyHintModal';
import { mobileAdmin } from '@/lib/mobileAdminPaths';
import { cn } from '@/lib/cn';

function BigAction({
  to,
  icon,
  title,
  subtitle,
  variant,
}: {
  to: string;
  icon: string;
  title: string;
  subtitle: string;
  variant: 'primary' | 'secondary' | 'admin';
}) {
  return (
    <Link
      to={to}
      className={cn(
        'flex min-h-[5.5rem] touch-manipulation flex-col items-stretch justify-center gap-1 rounded-3xl border-2 px-5 py-5 text-left shadow-md transition active:scale-[0.99]',
        variant === 'primary' &&
          'border-teal-400/40 bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-teal-900/25',
        variant === 'secondary' &&
          'border-slate-200/90 bg-white text-slate-900 shadow-slate-900/10',
        variant === 'admin' &&
          'border-violet-300/60 bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-indigo-900/25'
      )}
    >
      <span className="flex items-center gap-3">
        <span
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-xl',
            variant === 'secondary' ? 'bg-slate-100 text-slate-700' : 'bg-white/20 text-white'
          )}
        >
          <i className={cn(icon, variant === 'secondary' ? 'text-teal-600' : '')} aria-hidden />
        </span>
        <span className="text-xl font-bold tracking-tight">{title}</span>
      </span>
      <span
        className={cn(
          'mt-1 pl-[3.75rem] text-sm leading-snug',
          variant === 'secondary' ? 'text-slate-600' : 'text-white/90'
        )}
      >
        {subtitle}
      </span>
    </Link>
  );
}

export function MobileDashboardPage() {
  const { currentSiteId, needsOwnSite, capabilities } = useSession();
  const [pcOpen, setPcOpen] = useState(false);

  if (currentSiteId < 1) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">日常管理</h1>
          <p className="mt-2 text-base text-slate-600">
            电脑端完成站点与公众号配置后，用手机做登记与查稿即可。
          </p>
        </div>
        <Card title="提示">
          <p className="text-base leading-relaxed text-slate-700">
            {needsOwnSite
              ? '当前账号还没有站点。请先在电脑浏览器登录，在「站点管理」中创建站点并配置公众号。'
              : '请先在上方「当前工作站点」中选择要操作的站点。'}
          </p>
          <button
            type="button"
            className="mt-5 w-full min-h-12 rounded-2xl bg-slate-100 py-3 text-base font-semibold text-slate-800 active:bg-slate-200"
            onClick={() => setPcOpen(true)}
          >
            为何需要电脑端？
          </button>
        </Card>
        <PcOnlyHintModal
          open={pcOpen}
          onClose={() => setPcOpen(false)}
          feature="站点创建、密钥与公众号接入、字段映射等首次配置"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">日常管理</h1>

      <div className="flex flex-col gap-4">
        <BigAction
          to={mobileAdmin.register}
          icon="fa-solid fa-pen-to-square"
          title="登记信息"
          subtitle="填写标题、正文与配图，保存后去查询里推公众号草稿"
          variant="primary"
        />
        <BigAction
          to={mobileAdmin.entries}
          icon="fa-solid fa-list"
          title="信息查询"
          subtitle="搜索已登记内容，上传公众号草稿箱、删除"
          variant="secondary"
        />
        {capabilities.manage_users ? (
          <BigAction
            to={mobileAdmin.users}
            icon="fa-solid fa-users-gear"
            title="用户管理"
            subtitle="新建与维护普通用户账号"
            variant="admin"
          />
        ) : null}
      </div>

      <div className="relative mt-8 border-t border-slate-200/90 pt-6">
        <div className="rounded-2xl border border-dashed border-slate-200/90 bg-slate-100/70 px-4 py-4 text-center shadow-inner shadow-slate-900/[0.02]">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">电脑端配置</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            其它设置（站点密钥、公众号、样式模板等）请在电脑浏览器中操作。
          </p>
        </div>
      </div>
    </div>
  );
}
